import {
    Component,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { RouterLink } from "@angular/router";
import { SelectorComponent } from "@components/selector/selector.component";
import { Subject, firstValueFrom } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { TreeSelectorComponent } from "~app/common/components/tree-selector/tree-selector.component";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ArbetsorderDisabledFlag } from "~app/features/planera/model/ArbetsorderDisabledFlag";
import { IProduktionsmall } from "../arbetsorder-produktval/arbetsorder-produktval.models";
import { ArbetsorderProduktvalService } from "../arbetsorder-produktval/arbetsorder-produktval.service";
import { UpdateArbetsorderProduktsattCommand } from "../commands/UpdateArbetsorderProduktsattCommand";
import { IArbetsorderHandelssortimentsgrupp } from "../model/IArbetsorderHandelssortimentsgrupp";
import { ArbetsorderWrapper } from "../planera-detail/planera-detail-models";
import { ArbetsorderMediatorService } from "../services/arbetsorder-mediator.service";
import { ArbetsorderHandelssortimentsvalRadComponent } from "./arbetsorder-handelssortimentsval-rad/arbetsorder-handelssortimentsval-rad.component";

@Component({
    selector: "rask-arbetsorder-handelssortimentsval",
    templateUrl: "./arbetsorder-handelssortimentsval.component.html",
    styleUrls: ["./arbetsorder-handelssortimentsval.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectorComponent,
        RouterLink,
        ArbetsorderHandelssortimentsvalRadComponent
    ]
})
export class ArbetsorderHandelssortimentsvalComponent implements OnInit {
    arbetsorderWrapper: ArbetsorderWrapper;
    handelssortimentsgrupper: IArbetsorderHandelssortimentsgrupp[];
    produktionsmallar: IProduktionsmall[];
    selectedProduktionsmall: IProduktionsmall;
    @ViewChild("tree") treeSelector: TreeSelectorComponent;

    private readonly arbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly arbetsorderProduktvalService = inject(ArbetsorderProduktvalService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly raskModalService = inject(RaskModalService);

    constructor() {
        this.arbetsorderProduktvalService.produktionsMallar$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(x => {
                this.produktionsmallar = x;
            });
    }

    get totalsummaAsText(): string {
        const total = this.arbetsorderWrapper.arbetsorder.handelssortiment.reduce((accumulator, obj) => accumulator + (obj.angivenKvantitet || 0), 0);
        return total.toString();
    }

    canEdit = () => this.authenticationService.canEdit() &&
        this.arbetsorderWrapper?.arbetsorder?.isEditableForMode(ArbetsorderDisabledFlag.makulerad) &&
        !this.arbetsorderWrapper.arbetsorder.isExternArbetsorder;

    ngOnInit(): void {
        this.arbetsorderProduktvalService.refreshproduktionsMallar();
        this.arbetsorderMediatorService.arbetsorder
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(changeEvent => {
                if (!this.arbetsorderWrapper?.arbetsorder || changeEvent?.isUpdated()) {
                    this.arbetsorderWrapper = changeEvent.arbetsorderWrapper;
                    this.fetchHandelssortimentsgrupper();
                }
            });

        this.fetchHandelssortimentsgrupper();
    }

    async onProduktionsmallChanged(mall: IProduktionsmall): Promise<void> {
        if (mall === null || mall === undefined) {
            return;
        }
        const result = await firstValueFrom(this.raskModalService.confirm(new ModalSettings("Vill du ersätta val?",
            "Befintliga val av handelssortiment ersätts med vald produktionsmall")));

        if (result === ConfirmDialogResponse.save) {
            this.selectedProduktionsmall = mall;
        }
    }

    onSelectionEvent($event: any) {
        const shouldBeproduktsatt = this.arbetsorderWrapper.arbetsorder.handelssortiment.length > 0;
        if (shouldBeproduktsatt !== this.arbetsorderWrapper.arbetsorder.produktsatt) {
            this.arbetsorderWrapper.arbetsorder.produktsatt = shouldBeproduktsatt;
            this.arbetsorderWrapper.arbetsorder.commandManager.updateCommand(
                new UpdateArbetsorderProduktsattCommand((c) => {
                    c.arbetsorderId = this.arbetsorderWrapper.arbetsorder.id;
                    c.produktsatt = shouldBeproduktsatt;
                })
            );
        }
    }

    selectionFormatter = (item: IProduktionsmall) => item;

    valueFormatter = (item: IProduktionsmall) => item.benamning;

    private fetchHandelssortimentsgrupper(): void {
        this.busyService.addWork(
            this.arbetsorderProduktvalService.getHandelssortimentsgrupper(this.arbetsorderWrapper?.arbetsorder.id, DateUtils.getStartOfMonth(), DateUtils.getStartOfNextMonth())
        ).subscribe(result => {
            this.handelssortimentsgrupper = result;
        });
    }
}
